import type { BpfCycleCountMissionFragment, BpfCycleCountMissionTaskFragment } from "~/services/graphql";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { updateSite } from "~/store/siteSlice";

/**
 * Offline READ cache for the material cycle-count mission picker + task list — the parity gap with
 * legacy Dexie `cycleCountTaskLists` (Atlas: PWA Offline-First Mobile Module Development — "cache
 * reference data in a Redux slice, add the slice to the persist whitelist"). RTK Query's own cache is
 * deliberately NEVER persisted app-wide (`store/index.ts`); this is a purpose-built, narrowly-scoped
 * mirror populated only from confirmed successful fetches (`useCycleCountMissions` /
 * `useCycleCountMissionTasks`), so a cold offline start still has something real to render.
 *
 * Persisted under its OWN nested `redux-persist` key (`store/index.ts`) — separate from the outer
 * `settings`/`site` persistence — so `SFAuth.redirectToLogout()` can remove just this slice's storage
 * entry on logout (PKCE logout deliberately does not dispatch the Redux `LOGOUT` root-reset; see
 * `store/index.ts`) without touching unrelated persisted prefs.
 *
 * The task list is a single slot, not a per-mission map: at most one mission is realistically worked
 * per session (`useCycleCountMissions`' docstring), and `siteLocationId` is carried alongside so a
 * stale snapshot from a different station is never served after a site change (belt-and-suspenders —
 * `updateSite` already clears this slice outright, see below).
 */

export interface CycleCountTaskListSnapshot {
	missionKey: string;
	siteLocationId: string;
	tasks: BpfCycleCountMissionTaskFragment[];
}

export interface CycleCountCacheState {
	missions: BpfCycleCountMissionFragment[] | null;
	taskList: CycleCountTaskListSnapshot | null;
}

const initialState: CycleCountCacheState = { missions: null, taskList: null };

const cycleCountCacheSlice = createSlice({
	name: "cycleCountCache",
	initialState,
	reducers: {
		missionsCached: (state, action: PayloadAction<BpfCycleCountMissionFragment[]>) => {
			state.missions = action.payload;
		},
		taskListCached: (state, action: PayloadAction<CycleCountTaskListSnapshot>) => {
			state.taskList = action.payload;
		}
	},
	extraReducers: (builder) => {
		// Any site pick — the first one (nothing cached yet, a no-op) or a genuine switch to a
		// different station — drops the cache. Simpler and safer than comparing previous/next site
		// ids: a wrong station's task list must never be served from cache after the user switches.
		builder.addCase(updateSite, (state) => {
			state.missions = null;
			state.taskList = null;
		});
	}
});

export const { missionsCached, taskListCached } = cycleCountCacheSlice.actions;
export default cycleCountCacheSlice.reducer;
